import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  setGlobalError,
  setSuccessInfo
} from '../../Redux/Reducer/GlobalError';
import {
  API_ENDPOINTS,
  VIEW_TYPES
} from '../../Component/ChatbotSettingsComponent/utils';
import { getCookie } from '../index';

// Create the context
const ChatbotSettingsContext = createContext();

export const AIR_TENANT_ID = '6423d08b6ba5d39df3db3779';

// ponytail: client presets are hardcoded overrides on the default preset;
// move them to backend storage if more clients need their own.
const CLIENT_PRESET_OVERRIDES = {
  [AIR_TENANT_ID]: {
    primaryColor: '#3d3d3d',
    contactUsEnabled: false,
    pricingEnabled: false,
    mobile: { contactUsEnabled: false, pricingEnabled: false }
  }
};

// Provider component
export const ChatbotSettingsContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [view, setView] = useState({
    view: VIEW_TYPES.PRESET_CHATBOT_SETTINGS
  });
  const defaultPresetSettings = {
    contactUsEnabled: true,
    pricingEnabled: true,
    scheduleTourEnabled: true,
    primaryColor: '#3d3d3d',
    chatIconAlignment: 'Right',
    rightPadding: 20,
    bottomPadding: 20,
    greetingMessage: 'Hi there! How can I help you today?',
    leftPadding: null,
    isTookBookingAllowed: true,
    unitDropdown: true,
    defaultSource: null,
    widgetButtonsOutlined: false,
    zIndex: 99999,
    mobile: {
      zIndex: 99999,
      defaultWidget: 'booktour',
      showIcon: true,
      contactUsEnabled: true,
      pricingEnabled: true,
      scheduleTourEnabled: true,
      chatIconAlignment: 'Right',
      leftPadding: null,
      rightPadding: 20,
      bottomPadding: 20
    }
  };
  const clientOverrides = CLIENT_PRESET_OVERRIDES[view?.tenantId] || {};
  const presetSettings = {
    ...defaultPresetSettings,
    ...clientOverrides,
    mobile: { ...defaultPresetSettings.mobile, ...clientOverrides.mobile }
  };
  const [settingsFormData, setSettingsFormData] = useState(presetSettings);
  const [usePresetSettings, setUsePresetSettings] = useState(false);

  const handlePublishUpdates = async settingsFormDataParam => {
    const settingsFormDataTemp = settingsFormDataParam || settingsFormData;

    // Dynamically construct the `widgets_to_show` array based on `settingsFormData`
    const widgets_to_show = [];
    if (settingsFormDataTemp.contactUsEnabled) widgets_to_show.push('textus');
    if (settingsFormDataTemp.pricingEnabled) widgets_to_show.push('pna');
    if (settingsFormDataTemp.scheduleTourEnabled)
      widgets_to_show.push('booktour');

    const mobile_widgets_to_show = [];
    if (settingsFormDataTemp.mobile?.contactUsEnabled)
      mobile_widgets_to_show.push('textus');
    if (settingsFormDataTemp.mobile?.pricingEnabled)
      mobile_widgets_to_show.push('pna');
    if (settingsFormDataTemp.mobile?.scheduleTourEnabled)
      mobile_widgets_to_show.push('booktour');

    const payload = {
      communityData: {
        community_id: view?.communityId,
        tenant_id: view?.tenantId,
        can_book_tour: settingsFormDataTemp.isTookBookingAllowed
      },
      frontendData: {
        header_color: settingsFormDataTemp.primaryColor,
        bottom_position: settingsFormDataTemp.bottomPadding - 20,
        right_position:
          settingsFormDataTemp.chatIconAlignment === 'Right'
            ? settingsFormDataTemp.rightPadding - 20
            : null,
        left_position:
          settingsFormDataTemp.chatIconAlignment === 'Left'
            ? settingsFormDataTemp.leftPadding - 20
            : null,
        widgets_to_show,
        first_message: settingsFormDataTemp.greetingMessage,
        unit_dropdown_toggle: settingsFormDataTemp.unitDropdown,
        default_source: settingsFormDataTemp.defaultSource,
        widget_buttons_outlined: settingsFormDataTemp.widgetButtonsOutlined,
        z_index: settingsFormDataTemp.zIndex
      }
    };

    payload.frontendData.mobile = {
      widgets_to_show: mobile_widgets_to_show,
      show_icon: settingsFormDataTemp.mobile.showIcon,
      default_widget: settingsFormDataTemp.mobile.defaultWidget,
      bottom_position: settingsFormDataTemp.mobile.bottomPadding - 20,
      right_position:
        settingsFormDataTemp.mobile.chatIconAlignment === 'Right'
          ? settingsFormDataTemp.mobile.rightPadding - 20
          : null,
      left_position:
        settingsFormDataTemp.mobile.chatIconAlignment === 'Left'
          ? settingsFormDataTemp.mobile.leftPadding - 20
          : null,
      z_index: settingsFormDataTemp.mobile.zIndex
    };

    try {
      const response = await fetch(
        API_ENDPOINTS.SAVE_CONFIG_DETAILS(view?.communityId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${getCookie('rb_access_token')}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const data = await response.json();
        dispatch(setSuccessInfo('Settings saved successfully!'));
        console.log('API Response:', data);
      } else {
        const errorData = await response.json();
        dispatch(
          setGlobalError(`Failed to save settings: ${errorData.message}`)
        );
        console.error('API Error:', errorData);
      }
    } catch (error) {
      dispatch(setGlobalError('An error occurred while saving settings.'));
      console.error('Fetch Error:', error);
    }
  };

  return (
    <ChatbotSettingsContext.Provider
      value={{
        setView,
        view,
        settingsFormData,
        setSettingsFormData,
        usePresetSettings,
        setUsePresetSettings,
        presetSettings,
        handlePublishUpdates
      }}
    >
      {children}
    </ChatbotSettingsContext.Provider>
  );
};

// Custom hook to use the context
export const useChatbotSettingsContext = () => {
  const context = useContext(ChatbotSettingsContext);
  if (!context) {
    throw new Error(
      'useChatbotSettingsContext must be used within a ChatbotSettingsContextProvider'
    );
  }
  return context;
};
